/**
 * Per-route SEO metadata (titles, descriptions, keywords).
 * Used by SEOHead via lookup keys.
 * Titles here are PRE-suffix ("| Ads Dashbound" appended automatically),
 * EXCEPT entries marked with `fullTitle: true` which use the title verbatim.
 */

export const SEO = {
  '/': {
    fullTitle: true,
    title: 'Ads Dashbound — Premium Performance Traffic Platform for Advertisers',
    description: 'Scale campaigns with curated traffic formats, source-level controls, AI-powered routing, fraud filtering, and flexible top-up based launches.',
    keywords: 'performance ad network, premium traffic platform, traffic for advertisers, ad network, performance marketing platform, media buying platform',
  },

  // === Ad formats ===
  '/ad-formats': {
    title: 'Ad Formats for Performance Advertisers',
    description: 'Six performance traffic formats: zero-click, domain redirect, pop, push, in-page push, and direct click — with source-level controls.',
    keywords: 'ad formats, performance traffic formats, traffic types for advertisers',
  },
  '/ad-formats/zero-click': {
    title: 'Zero-Click Traffic for Advertisers',
    description: 'Reach high-intent users with zero-click traffic flows, source-level controls, GEO targeting, and managed campaign support.',
    keywords: 'zero click traffic, buy zero click traffic, zero-click ads, zero click ad network, direct response traffic',
  },
  '/ad-formats/domain-redirect': {
    title: 'Domain Redirect Traffic for Performance Campaigns',
    description: 'Buy domain redirect traffic for offer-focused campaigns with smart routing, GEO controls, source-level filtering, and advertiser support.',
    keywords: 'domain redirect traffic, buy domain redirect traffic, domain redirect ads, redirect traffic',
  },
  '/ad-formats/pop': {
    title: 'Pop Traffic for Advertisers',
    description: 'Scale reach with pop traffic for testing, retargeting, and direct-response campaigns across selected GEOs and sources.',
    keywords: 'pop traffic, buy pop traffic, popunder traffic, pop ads, popunder ads',
  },
  '/ad-formats/push': {
    title: 'Push Traffic for Performance Advertisers',
    description: 'Launch push traffic campaigns with curated sources, GEO targeting, device controls, and managed optimization support.',
    keywords: 'push traffic, buy push traffic, push notification ads, push ad network',
  },
  '/ad-formats/in-page-push': {
    title: 'In-Page Push Traffic',
    description: 'Use in-page push traffic to reach users across web environments with clean placements, format flexibility, and source-level controls.',
    keywords: 'in-page push traffic, in-page push ads, in page push, IPP traffic',
  },
  '/ad-formats/direct-click': {
    title: 'Direct Click Traffic & Smart Redirects',
    description: 'Send users directly to advertiser landing pages with direct click traffic, smart routing, and managed campaign setup.',
    keywords: 'direct click traffic, smart redirect traffic, direct click ads',
  },

  // === Verticals ===
  '/verticals': {
    title: 'Performance Marketing Verticals',
    description: 'Vertical-aware traffic for iGaming, finance, e-commerce, software, VPN, sweepstakes, and lead generation campaigns.',
    keywords: 'performance marketing verticals, ad network verticals, advertising verticals',
  },
  '/verticals/igaming': {
    title: 'iGaming Traffic for Advertisers',
    description: 'Launch iGaming and betting campaigns with selected traffic formats, GEO planning, source controls, and top-up based support.',
    keywords: 'iGaming traffic, betting traffic, casino traffic, sportsbook traffic, gambling ad traffic',
  },
  '/verticals/finance': {
    title: 'Finance & Trading Traffic for Advertisers',
    description: 'Reach finance and trading audiences with performance traffic formats, GEO targeting, source-level insights, and managed campaign support.',
    keywords: 'finance traffic, trading traffic, fintech ads, forex traffic, crypto offer traffic',
  },
  '/verticals/ecommerce': {
    title: 'E-commerce Traffic for Product Offers',
    description: 'Drive traffic to e-commerce offers using curated ad formats, GEO targeting, source controls, and flexible top-up based launches.',
    keywords: 'ecommerce traffic, product offer traffic, ecommerce ads, online shop traffic',
  },
  '/verticals/vpn': {
    title: 'VPN Traffic for Performance Campaigns',
    description: 'Promote VPN offers with source-level controls, GEO routing, traffic format planning, and advertiser support.',
    keywords: 'VPN traffic, VPN advertising, VPN ad network',
  },
  '/verticals/software-utilities': {
    title: 'Software & Utilities Traffic',
    description: 'Scale software, app, and utility offers with performance traffic formats, fraud filtering, and managed source planning.',
    keywords: 'software traffic, utility offer traffic, software ads, app install traffic, utility ads',
  },
  '/verticals/sweepstakes': {
    title: 'Sweepstakes Traffic for Lead Campaigns',
    description: 'Launch sweepstakes and lead-generation campaigns with curated traffic formats, GEO targeting, and source-level controls.',
    keywords: 'sweepstakes traffic, sweepstakes ads, sweeps offer traffic, SOI sweepstakes',
  },
  '/verticals/lead-generation': {
    title: 'Lead Generation Traffic for Advertisers',
    description: 'Generate leads with selected traffic formats, source controls, GEO targeting, and managed top-up based campaign launches.',
    keywords: 'lead generation traffic, lead gen ads, lead generation ad network, performance lead gen',
  },

  // === Features ===
  '/features': {
    title: 'Platform Features',
    description: 'AI traffic intelligence, source-level control, fraud filtering, and a top-up advertising model for performance teams.',
    keywords: 'ad platform features, performance traffic features',
  },
  '/features/ai-traffic-intelligence': {
    title: 'AI Traffic Intelligence for Performance Advertisers',
    description: 'AI-assisted source scoring, smart GEO and device routing, fraud signal detection, and budget flow suggestions for cleaner traffic.',
    keywords: 'AI ad optimization, AI traffic optimization, AI media buying, smart traffic routing, performance AI',
  },
  '/features/source-level-control': {
    title: 'Source-Level Traffic Control',
    description: 'Whitelist, blacklist, and placement-level controls for cleaner traffic paths and source-level optimization across campaigns.',
    keywords: 'source level traffic control, traffic source optimization, whitelist blacklist traffic, placement control',
  },
  '/features/fraud-filtering': {
    title: 'Ad Fraud Filtering & Bot Traffic Detection',
    description: 'Continuous quality controls, bot traffic filtering, and source monitoring for cleaner performance campaigns.',
    keywords: 'ad fraud filtering, bot traffic filtering, anti fraud ad network, traffic quality',
  },
  '/features/top-up-model': {
    title: 'Top-Up Advertising Model',
    description: 'No subscription plans. Top-up funded campaigns based on traffic format, GEO, and budget — with manual advertiser review.',
    keywords: 'top up advertising, top up ad network, prepaid ad platform, top-up campaign funding',
  },
  '/features/smart-optimization': {
    title: 'Smart Campaign Optimization',
    description: 'Format and GEO recommendations, scaling guidance, and source-level decisioning to optimize performance campaigns.',
    keywords: 'campaign optimization, smart traffic optimization, scaling guidance',
  },

  // === Static pages ===
  '/about': {
    title: 'About Ads Dashbound',
    description: 'Ads Dashbound is a premium performance traffic platform built for serious advertisers. Curated formats, source-level controls, and human-led support.',
    keywords: 'about Ads Dashbound, performance traffic company',
  },
  '/blog': {
    title: 'Performance Marketing Blog',
    description: 'Practical playbooks on traffic formats, GEO testing, source-level optimization, and compliant scaling for advertisers and media buyers.',
    keywords: 'performance marketing blog, media buying blog, traffic formats blog',
  },
  '/faq': {
    title: 'Advertiser FAQ',
    description: 'Answers to common questions about advertiser application, top-up funding, traffic formats, GEOs, tracking, and compliance.',
    keywords: 'advertiser FAQ, ad network FAQ, performance traffic FAQ',
  },
  '/contact': {
    title: 'Contact Ads Dashbound',
    description: 'Get in touch with the Ads Dashbound team for advertiser inquiries, partnerships, or general questions.',
    keywords: 'contact Ads Dashbound, performance traffic contact',
  },
  '/signup': {
    title: 'Apply as Advertiser',
    description: 'Submit an advertiser application to Ads Dashbound. Share your offers, target GEOs and budget — our team reviews and recommends the right traffic formats.',
    keywords: 'advertiser application, apply as advertiser, ad network signup, advertiser sign up',
  },
  '/apply': {
    title: 'Apply as Advertiser',
    description: 'Apply to advertise on Ads Dashbound. Share your offers, target GEOs and budget — our team reviews and recommends the right traffic formats.',
    keywords: 'advertiser application, apply as advertiser, ad network signup',
  },
  '/privacy-policy': {
    title: 'Privacy Policy',
    description: 'How Ads Dashbound collects, uses, and protects information submitted through Dashbound.media.',
    keywords: 'privacy policy, ad network privacy',
  },
  '/terms-and-conditions': {
    title: 'Terms & Conditions',
    description: 'Terms governing your use of the Ads Dashbound public website and advertiser application process.',
    keywords: 'terms and conditions, ad network terms',
  },
  '/acceptable-use-policy': {
    title: 'Acceptable Use Policy',
    description: 'Acceptable use rules for advertisers running campaigns on Ads Dashbound.',
    keywords: 'acceptable use policy, advertising policy',
  },
  '/cookie-policy': {
    title: 'Cookie Policy',
    description: 'How Ads Dashbound uses cookies and similar technologies on this marketing website. Manage your cookie preferences for analytics, marketing, and preference categories.',
    keywords: 'cookie policy, cookie preferences, ad network cookies, gdpr cookies',
  },

  /* Lead Generation index + sub-verticals */
  '/lead-generation': {
    title: 'Lead Generation Traffic for Advertisers',
    description: 'Performance traffic for lead generation across high-value verticals — real estate, credit cards, and insurance. Curated formats, source-level controls, and human campaign review.',
    keywords: 'lead generation traffic, performance lead gen, real estate leads, credit card leads, insurance leads',
  },
  '/lead-generation/real-estate': {
    title: 'Real Estate Lead Generation — Performance Traffic',
    description: 'Performance marketing for real estate — qualified buyer and renter leads for developers, brokers, and proptech platforms. Push, in-page push, direct click, and source-level controls.',
    keywords: 'real estate lead generation, property leads, buyer leads, broker leads, real estate performance marketing',
  },
  '/lead-generation/credit-cards': {
    title: 'Credit Card Lead Generation — Compliant Performance Traffic',
    description: 'Performance traffic for credit-card issuers, comparison sites, and fintech apps — approved-card and pre-qualified leads with compliance, fraud filtering, and source-level controls.',
    keywords: 'credit card lead generation, credit card leads, fintech leads, card application traffic, compliant credit card ads',
  },
  '/lead-generation/insurance': {
    title: 'Insurance Lead Generation — Quote-Intent Traffic',
    description: 'Performance traffic for auto, home, health, life, and travel insurance funnels — quote-request and policy-sign-up leads with strict GEO and licensing controls.',
    keywords: 'insurance lead generation, insurance leads, quote leads, auto insurance traffic, life insurance leads',
  },

  /* Case Studies */
  '/case-studies': {
    title: 'Case Studies — Lead Generation Performance Campaigns',
    description: 'Illustrative composites from advertiser launches across real estate, credit cards, and insurance — challenge, approach, KPIs, and what we learned.',
    keywords: 'performance marketing case studies, lead generation case studies, ad network case studies, real estate lead gen case study, credit card acquisition case study, insurance lead gen case study',
  },
  '/case-studies/real-estate-developer-lead-gen-mena': {
    title: 'Real Estate Developer Lead Generation — MENA Case Study',
    description: 'How a MENA-focused property developer reduced cost-per-qualified-lead by ~38% in 90 days using push, in-page push, and source-level controls. Illustrative composite.',
    keywords: 'real estate lead generation case study, MENA real estate marketing, property developer ad campaign, qualified lead campaign',
  },
  '/case-studies/credit-card-comparison-site-scaling': {
    title: 'Credit Card Comparison Site Scaling — Approved-Card Volume Case Study',
    description: 'How a credit-card comparison platform scaled approved-card volume ~3.4× over six months with soft-pull pre-qualification and curated source supply. Illustrative composite.',
    keywords: 'credit card lead generation case study, credit card comparison marketing, fintech acquisition scaling, soft pull pre qualification campaign',
  },
  '/case-studies/insurance-broker-quote-funnel-optimization': {
    title: 'Insurance Broker Quote Funnel Optimization — Case Study',
    description: 'How a multi-product insurance brokerage lifted bound-policy rate by ~52% with product-split funnels and source-level controls. Illustrative composite.',
    keywords: 'insurance lead generation case study, insurance broker marketing, bound policy optimization, multi product insurance campaign',
  },

  /* New 2026 pillar blog posts */
  '/blog/ultimate-guide-performance-marketing-2026': {
    title: 'The Ultimate Guide to Performance Marketing in 2026',
    description: 'A comprehensive 2026 playbook covering channels, attribution, source-level controls, AI-assisted routing, and the rise of curated traffic platforms.',
    keywords: 'performance marketing guide 2026, performance marketing in 2026, paid acquisition guide, performance marketing playbook',
  },
  '/blog/how-media-buying-agencies-scale-campaigns': {
    title: 'How Media Buying Agencies Scale Performance Campaigns',
    description: 'The 4-stage scaling framework senior media buyers use to take a $500/day proof-of-concept to $50,000/day without breaking the funnel.',
    keywords: 'media buying agency, scale performance campaigns, performance campaign scaling, scale media buying',
  },
  '/blog/native-vs-display-advertising': {
    title: 'Native Advertising vs Display Advertising — When to Use Each',
    description: 'A side-by-side comparison of native and display advertising — formats, intent, CTR, scaling, and which fits your offer.',
    keywords: 'native advertising vs display, native ads, display ads, native vs display 2026, performance native advertising',
  },
};

export function getSEO(path) {
  return SEO[path] || null;
}
